import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { NgbAccordionConfig, NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { PropertyMenu } from 'src/app/core/models/store-templates/property-menu';
import { TemplateNavigation } from 'src/app/core/models/store-templates/template-navigation';
import { TemplatePagesSelection } from 'src/app/core/models/store-templates/template-pages-selection';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-navigation-menus-config',
  templateUrl: './navigation-menus-config.component.html',
  styleUrls: ['./navigation-menus-config.component.scss']
})
export class NavigationMenusConfigComponent implements OnInit {
  @Input() menusConfig: PropertyMenu;
  @Input() siteId: string;
  @Input() pagesListDll: Array<TemplatePagesSelection>;

  newLink: TemplateNavigation;
  expandedIndex: number = -1;

  constructor(private config: NgbAccordionConfig, private translate: TranslateService) {
    config.closeOthers = true;
  }

  ngOnInit(): void { }

  /**
   * Handles reordering of links via Drag & Drop
   */
  drop(event: CdkDragDrop<TemplateNavigation[]>) {
    moveItemInArray(this.menusConfig.navigations, event.previousIndex, event.currentIndex);
    // If we moved the currently expanded item, update its index
    if (this.expandedIndex === event.previousIndex) {
      this.expandedIndex = event.currentIndex;
    } else if (
      event.previousIndex < this.expandedIndex &&
      event.currentIndex >= this.expandedIndex
    ) {
      this.expandedIndex--;
    } else if (
      event.previousIndex > this.expandedIndex &&
      event.currentIndex <= this.expandedIndex
    ) {
      this.expandedIndex++;
    }
  }

  /**
   * Toggles the expansion state of a link item
   */
  toggleExpand(index: number) {
    this.expandedIndex = this.expandedIndex === index ? -1 : index;
  }

  /**
   * Adds a new link to the menu and expands it
   */
  add(event: MouseEvent) {
    this.newLink = {
      label: "",
      target: "INTERNAL_PAGE", // Default target
      path: ""
    } as TemplateNavigation;
    
    this.menusConfig.navigations.push(this.newLink);
    this.expandedIndex = this.menusConfig.navigations.length - 1;
    event.preventDefault();
  }

  /**
   * Removes a link from the menu
   */
  close(event: MouseEvent, index: number) {
    this.menusConfig.navigations.splice(index, 1);
    if (this.expandedIndex === index) {
      this.expandedIndex = -1;
    } else if (this.expandedIndex > index) {
      this.expandedIndex--;
    }
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Translates the target type for display in the badge
   */
  getTargetLabel(targetValue: string): string {
    switch (targetValue) {
      case 'PLP':
        return this.translate.instant('menu_products_list_page');
      case 'PDP':
        return this.translate.instant('menu_product_details_page');
      case 'INTERNAL_PAGE':
        return this.translate.instant('menu_static_pages');
      case 'EXTERNAL_PAGE':
        return this.translate.instant('menu_external_url');
      default:
        return targetValue ? targetValue.toLowerCase() : '';
    }
  }

  /**
   * Returns a friendly label for the path or context of the link
   */
  getPathLabel(navItem: TemplateNavigation): string {
    if (!navItem) return '';

    // If it's an internal page, find the label in the pagesListDll
    if (navItem.target === 'INTERNAL_PAGE' && navItem.path) {
      const page = this.pagesListDll?.find(p => p.path === navItem.path);
      return page ? page.label : navItem.path;
    }

    // If it's an external URL
    if (navItem.target === 'EXTERNAL_PAGE' && navItem.path) {
      return navItem.path;
    }

    // If it's a filter-based page (PLP/PDP)
    if ((navItem.target === 'PLP' || navItem.target === 'PDP') && navItem['filter']?.by) {
      const filterByLabel = this.translate.instant('menu_filter_by') + ': ' + this.getFilterLabel(navItem['filter'].by);
      return filterByLabel;
    }

    return '';
  }

  /**
   * Translates the filter type into a human-readable label
   */
  getFilterLabel(filterBy: string): string {
    switch (filterBy) {
      case 'CATEGORY_IDS':
        return this.translate.instant('sh_categories');
      case 'PRODUCT_IDS':
        return this.translate.instant('sb_products');
      case 'PRODUCT_ID':
        return this.translate.instant('sh_product');
      default:
        return filterBy ? filterBy.toLowerCase() : '';
    }
  }
}
